import { useEffect, useRef, useState, useCallback } from "react";
import { X, MessageCircle, Printer, Truck, RotateCcw, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, inr, fmtDate, errMsg, waLink, Badge, Select, Btn, STATUS_LABEL, PAY_LABEL, PAY_TONE } from "./shared";
import { ShipPanel } from "./ShipPanel";
import { Invoice } from "./Invoice";

const Row = ({ k, v }) => (
    <div className="flex justify-between gap-6 text-sm py-1.5"><span className="text-ash">{k}</span><span className="text-charcoal text-right">{v}</span></div>
);

export const OrderDetail = ({ orderId, statuses, paymentStatuses, shipping, onClose, onChanged }) => {
    const [o, setO] = useState(null);
    const [manual, setManual] = useState({ courier: "", awb_code: "" });
    const [busy, setBusy] = useState(false);
    const [printing, setPrinting] = useState(false);
    const invoiceRef = useRef(null);

    const load = useCallback(() => api.get(`/admin/orders/${orderId}`).then(setO).catch(() => toast.error("Could not load order")), [orderId]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, ok) => {
        setBusy(true);
        try {
            await fn();
            if (ok) toast.success(ok);
            await load();
            onChanged();
        } catch (e) {
            toast.error(errMsg(e, "Action failed"));
        } finally {
            setBusy(false);
        }
    };

    const print = () => {
        setPrinting(true);
        setTimeout(() => { window.print(); setPrinting(false); }, 150);
    };

    if (!o) return <div className="fixed inset-0 z-[60] bg-charcoal/50 backdrop-blur-sm" onClick={onClose} />;
    const canRefund = o.payment_status === "paid" && o.payment_method === "razorpay";

    return (
        <>
            <div className="fixed inset-0 z-[60] bg-charcoal/50 backdrop-blur-sm flex justify-end print:hidden" data-testid="order-detail-overlay">
                <div data-lenis-prevent className="w-full max-w-2xl h-full overflow-y-auto overscroll-contain bg-ivory p-8 sm:p-10 space-y-8" data-testid="order-detail">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-[10px] tracking-[0.3em] uppercase text-golddark mb-1">Order</p>
                            <h2 className="font-serif text-3xl text-charcoal">#{o.ref}</h2>
                            <p className="text-xs text-ash mt-1">{fmtDate(o.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Badge status={o.status} testId="order-detail-status" />
                            <button onClick={onClose} data-testid="order-detail-close" className="text-ash hover:text-charcoal"><X size={20} /></button>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-8">
                        <div>
                            <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-3">Customer</p>
                            <p className="text-sm text-charcoal font-medium">{o.name}</p>
                            <p className="text-sm text-smoke">{o.phone}</p>
                            {o.email && <p className="text-sm text-smoke" data-testid="order-detail-email">{o.email}</p>}
                            <p className="text-sm text-smoke leading-relaxed mt-1">{o.address}</p>
                            {o.pincode && <p className="text-sm text-smoke">{o.city}{o.state ? `, ${o.state}` : ""} — {o.pincode}</p>}
                            <div className="flex gap-2 mt-4">
                                <Btn href={waLink(o)} testId="order-detail-whatsapp" tone="outline"><MessageCircle size={12} /> WhatsApp</Btn>
                                <Btn onClick={print} testId="order-detail-print" tone="ghost"><Printer size={12} /> Invoice</Btn>
                            </div>
                        </div>
                        <div>
                            <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-3">Payment</p>
                            <Row k="Method" v={<span className="capitalize">{o.payment_method}</span>} />
                            <Row k="Status" v={<span className={`font-medium ${PAY_TONE[o.payment_status]}`} data-testid="order-detail-payment-status">{PAY_LABEL[o.payment_status] ?? o.payment_status}</span>} />
                            {o.payment_id && <Row k="Payment ID" v={<span className="text-xs break-all">{o.payment_id}</span>} />}
                            {o.refund_id && <Row k="Refund ID" v={<span className="text-xs">{o.refund_id}</span>} />}
                            <div className="mt-3">
                                <Select testId="order-detail-payment-select" value={o.payment_status} options={paymentStatuses.map((s) => [s, PAY_LABEL[s]])} onChange={(v) => act(() => api.patch(`/admin/orders/${o.id}/payment`, { payment_status: v }), "Payment status updated")} className="w-full" />
                            </div>
                            <div className="mt-4 pt-3 border-t border-gold/15">
                                <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-2">Confirmation Email</p>
                                {(() => {
                                    const s = o.email_confirmation;
                                    const st = (s?.status || (s ? "pending" : "not sent"));
                                    const tone = ["delivered", "sent"].includes(st) ? "text-[#3E5F3A]" : ["failed", "skipped", "bounced"].includes(st) ? "text-[#8B2E2E]" : "text-golddark";
                                    return (
                                        <div className="text-sm space-y-1" data-testid="order-detail-confirmation">
                                            <Row k="Status" v={<span className={`font-medium capitalize ${tone}`} data-testid="order-detail-confirmation-status">{st}</span>} />
                                            {s?.to && <Row k="To" v={<span className="text-xs break-all">{s.to}</span>} />}
                                            {s?.error && <p className="text-[11px] text-[#8B2E2E]" data-testid="order-detail-confirmation-error">{s.error}</p>}
                                            <div className="pt-1">
                                                <Btn testId="order-detail-resend-confirmation" tone="outline" disabled={busy} onClick={() => act(() => api.post(`/admin/orders/${o.id}/resend-confirmation`), "Email re-sent")}><MessageCircle size={12} /> {s?.sent || s ? "Resend Email" : "Send Email"}</Btn>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    <div>
                        <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-3">Items</p>
                        <ul className="divide-y divide-gold/15 border-y border-gold/20">
                            {o.items.map((it, i) => (
                                <li key={i} className="flex items-center gap-4 py-3">
                                    {it.image && <img src={it.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${it.image}` : it.image} alt="" className="w-12 h-14 object-cover border border-gold/20" />}
                                    <div className="flex-1"><p className="text-sm text-charcoal">{it.name}</p><p className="text-[11px] text-ash">{it.product_id}{it.weight_kg ? ` · ${it.weight_kg} kg` : ""}</p></div>
                                    <span className="text-sm text-smoke">× {it.qty}</span>
                                    <span className="font-serif text-charcoal w-20 text-right">{inr(it.price * it.qty)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-3">
                            <Row k="Subtotal" v={inr(o.subtotal ?? o.total - (o.shipping_fee || 0))} />
                            <Row k={`Delivery${o.shipping_courier ? ` · ${o.shipping_courier}` : ""}`} v={inr(o.shipping_fee || 0)} />
                            <div className="flex justify-between border-t border-gold/20 pt-3 mt-1"><span className="text-[10px] tracking-[0.25em] uppercase text-smoke">Total</span><span className="font-serif text-2xl text-charcoal" data-testid="order-detail-total">{inr(o.total)}</span></div>
                        </div>
                    </div>

                    <div>
                        <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-3">Order status</p>
                        <Select testId="order-detail-status-select" value={o.status} options={statuses.map((s) => [s, STATUS_LABEL[s]])} onChange={(v) => act(() => api.patch(`/admin/orders/${o.id}/status`, { status: v }), `Marked ${STATUS_LABEL[v]}`)} className="w-full" />
                    </div>

                    <div>
                        <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-3 flex items-center gap-2"><Truck size={12} className="text-golddark" /> Shipping</p>
                        {o.shipment?.awb_code ? (
                            <div className="text-sm space-y-1" data-testid="order-detail-shipment">
                                <Row k="Courier" v={o.shipment.courier} />
                                <Row k="AWB / Tracking" v={<a href={o.shipment.tracking_url} target="_blank" rel="noopener noreferrer" className="text-golddark underline underline-offset-4">{o.shipment.awb_code}</a>} />
                                {o.shipment.etd && <Row k="Estimated delivery" v={o.shipment.etd} />}
                                {o.shipment.pickup && <Row k="Pickup" v={o.shipment.pickup} />}
                                <Row k="Weight" v={`${o.items.reduce((s, i) => s + (i.weight_kg || 0.15) * i.qty, 0).toFixed(2)} kg`} />
                                {o.shipment.manual && <p className="text-[11px] text-ash">Entered manually</p>}
                            </div>
                        ) : (
                            <div className="space-y-5">
                                {o.shipment?.shipment_id && <p className="text-[11px] text-[#3E5F3A]" data-testid="order-detail-pushed">✓ Sent to Shiprocket (order {o.shipment.sr_order_id}) — assign a courier below or in the Shiprocket panel.</p>}
                                {o.shipment?.push_error && !o.shipment?.shipment_id && <p className="text-[11px] text-[#8B2E2E]" data-testid="order-detail-push-error">Shiprocket auto-push failed: {o.shipment.push_error}</p>}
                                {shipping && ["paid", "cod_received", "confirmed", "processing", "packed"].includes(o.status) && (
                                    <div className="grid"><ShipPanel o={{ ...o, status: o.payment_method === "cod" ? "cod_received" : "paid" }} onShipped={() => { load(); onChanged(); }} /></div>
                                )}
                                <div>
                                    <p className="text-[11px] text-ash mb-2">Or enter courier details manually</p>
                                    <div className="flex flex-wrap gap-2">
                                        <input data-testid="manual-courier-input" value={manual.courier} onChange={(e) => setManual({ ...manual, courier: e.target.value })} placeholder="Courier (e.g. Delhivery)" className="flex-1 min-w-[140px] bg-transparent border-b border-charcoal/20 focus:border-gold outline-none py-2 text-sm text-charcoal" />
                                        <input data-testid="manual-awb-input" value={manual.awb_code} onChange={(e) => setManual({ ...manual, awb_code: e.target.value })} placeholder="AWB / tracking no." className="flex-1 min-w-[140px] bg-transparent border-b border-charcoal/20 focus:border-gold outline-none py-2 text-sm text-charcoal" />
                                        <Btn testId="manual-ship-button" tone="dark" disabled={busy || !manual.courier || manual.awb_code.length < 3} onClick={() => act(() => api.patch(`/admin/orders/${o.id}/shipment`, manual), "Shipment saved")}>Save & mark shipped</Btn>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2 pt-4 border-t border-gold/20">
                        {o.status !== "cancelled" && (
                            <Btn testId="order-detail-cancel" tone="danger" disabled={busy} onClick={() => window.confirm("Cancel this order? Stock will be returned.") && act(() => api.patch(`/admin/orders/${o.id}/status`, { status: "cancelled" }), "Order cancelled")}><Ban size={12} /> Cancel order</Btn>
                        )}
                        {canRefund && (
                            <Btn testId="order-detail-refund" tone="danger" disabled={busy} onClick={() => window.confirm(`Refund ${inr(o.total)} to the customer via Razorpay? This cannot be undone.`) && act(() => api.post(`/admin/orders/${o.id}/refund`), "Refund initiated")}><RotateCcw size={12} /> Refund via Razorpay</Btn>
                        )}
                    </div>
                </div>
            </div>
            {printing && <div className="fixed inset-0 z-[100] bg-white overflow-auto print:static"><Invoice ref={invoiceRef} o={o} /></div>}
        </>
    );
};
